<template>
    <v-app class="overflow-hidden" :theme="appThemeName">
        <v-main>
            <v-container
                fluid
                class="main-content my-0 pt-10"
                :class="{ [THEME_SCOPE_CLASS]: hasCheckoutTheme }"
            >
                <v-row class="align-center justify-center">
                    <v-col cols="12" xs="12">
                        <router-view></router-view>

                        <core-alert
                            v-model="alertConfiguration.visible"
                            :alert-message="alertConfiguration.message"
                            :alert-color="alertConfiguration.color"
                            :internationalised-message="
                                alertConfiguration.internationalisedMessage
                            "
                            :alert-parameters="
                                alertConfiguration.alertParameters
                            "
                        />
                    </v-col>
                </v-row>
            </v-container>

            <esenda-footer :class="{ [THEME_SCOPE_CLASS]: hasCheckoutTheme }" />
        </v-main>
    </v-app>
</template>

<script setup lang="ts">
import { onMounted, computed, ref } from "vue";
import { storeToRefs } from "pinia";
import {
    useSessionStore,
    useOrganisationStore,
    useCheckoutStore,
    useSessionClear,
} from "@/plugins/store";
import { useRouter, useRoute } from "vue-router";

import esendaFooter from "@/components/esendaFooter.vue";

import { setSentryUser } from "@/helpers/sentry";
import { getOrganisationData } from "@/apis/esendaV2/organisations";
import { routeNames } from "@/constants/routeNames";
import { useAlert } from "@esenda/core-ui/composables";
import { CoreAlert } from "@esenda/core-ui/components";
import { useEmbedSizeMonitor } from "@/composables/useEmbedSizeMonitor";
import {
    useCheckoutTheme,
    CHECKOUT_THEME_NAME,
    THEME_SCOPE_CLASS,
} from "@/theme/applyCheckoutTheme";

const { alertConfiguration, showAlert } = useAlert();
const { startEmbedSizeMonitor } = useEmbedSizeMonitor();

const organisationStore = useOrganisationStore();
const { setFormName } = organisationStore;
const checkoutStore = useCheckoutStore();
const sessionStore = useSessionStore();
const { sessionId, emailAddress } = storeToRefs(sessionStore);
const { setSessionId, setDefaultCountryCode, setDefaultLanguage } =
    sessionStore;
const { clearSessionData } = useSessionClear();

const router = useRouter();
const route = useRoute();

const shortcode = computed(() => route.params.shortcode);
const formName = computed(() => route.params.form);

/**
 * Checkout theme. Resolved once in main.ts before mount; read-only here.
 * - v-app uses the checkout theme (v-main background, Vuetify colours).
 * - .main-content gets the scope class, which carries the theme CSS variables.
 */
const checkoutTheme = useCheckoutTheme();
const hasCheckoutTheme = checkoutTheme !== null;
const appThemeName = hasCheckoutTheme ? CHECKOUT_THEME_NAME : undefined;

/** Shortcodes not associated to an organisation */
const shortcodeExceptions = ref([
    "",
    "unauthorised",
    "opayo-3ds-iframe",
    "lloyds-3ds-challenge-iframe",
    "lloyds-3ds-method-form-iframe",
    "terms-and-conditions",
    "privacy-policy",
    "reeds",
]);
/** Routes where we don't want to display the organisation logo */
const getAppHeaderRouteExceptions = ref([
    routeNames.LLOYDS_CHALLENGE_IFRAME,
    routeNames.LLOYDS_METHOD_FORM_IFRAME,
]);

const endSession = () => {
    /**
     * Clears the session data and redirects logs
     * out the user
     * @return {[void]}
     */
    const shortcodeParam =
        shortcode.value === undefined || shortcode.value === null
            ? organisationStore.shortcode
            : shortcode.value;
    const formNameParam =
        formName.value === undefined || formName.value === null
            ? organisationStore.formName
            : formName.value;
    const orderIdParam =
        checkoutStore.orderId !== undefined && checkoutStore.orderId !== null
            ? checkoutStore.orderId
            : String(route.params.orderId);

    clearSessionData();

    let redirectTo = routeNames.FORM;
    const pathParams = {
        shortcode: shortcodeParam,
        form: formNameParam,
        orderId: orderIdParam,
    };

    if (
        route.name !== redirectTo &&
        !getAppHeaderRouteExceptions.value.includes(route.name as routeNames)
    ) {
        showAlert("alerts.sessionTimeout");
        router.push({ name: redirectTo, params: pathParams });
    }
};

const detectIdleSession = () => {
    /**
     * Detects period of user inactivity and resets session
     * after 2h of inactivity
     * @return {[void]}
     * */

    let time;
    window.onload = resetTimer;

    // DOM Events
    document.onmousemove = resetTimer;
    document.onkeydown = resetTimer;
    document.onmousedown = resetTimer; // catches touchscreen presses as well
    document.ontouchstart = resetTimer; // catches touchscreen swipes as well
    document.ontouchmove = resetTimer; // required by some devices
    document.onclick = resetTimer; // catches touchpad clicks as well

    function resetTimer() {
        clearTimeout(time);
        // TODO ensure 2h is the right time
        time = setTimeout(endSession, 7200000);
    }
};

const getOrganisation = async () => {
    /**
     * Calls the organisation api. If the shortcode from the route is in the
     * list of exceptions we abort the function. If the api returns an organisation
     * we save it in the session storage. If it returns 404, we redirect the user
     * to the 404 page.
     * @returns {[void]}
     */
    const currentShortcode = String(shortcode.value);
    if (shortcodeExceptions.value.includes(currentShortcode)) {
        return;
    }

    await getOrganisationData(
        currentShortcode,
        String(organisationStore.formName),
    );
};

setDefaultCountryCode();
setDefaultLanguage();

onMounted(async () => {
    await router.isReady();

    if (sessionId.value === null) {
        setSessionId();
    }

    if (!window.location.href.includes("auth")) {
        setFormName(String(formName.value));
    }

    setSentryUser(String(emailAddress.value), String(sessionId.value));

    if (shortcode.value !== undefined) {
        await getOrganisation();
    }

    window.onload = function () {
        detectIdleSession();
    };

    startEmbedSizeMonitor();
});
</script>
